import React, { useEffect, useState } from "react";
import CustomTable from "../../../components/Tables/CustomTable";
import { MoreVertical } from "lucide-react";
import FilterByButton from "../../ui/FilterButton";
import DropdownMenu from "../../ui/DropdownMenu";
import Button from "../../ui/Button";
import DisableAgentModal from "./AgentDeleteSuccess";
import AddAgentModal from "./AddAgent";
import EditAgentModal from "./EditAgent";
import WhiteTitledCard from "../../../components/cards/WhiteTitledCard";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import {
  fetchAllCouriers,
  fetchCourierAgents,
  resetCourier,
} from "../../../redux/features/CourrierSlice";
import { mapDataToLabelValue } from "../../../helpers";
import moment from "moment";

const AgentTable: React.FC = () => {
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<any>();
  const [disabledAgent, setDisabledAgent] = useState(false);
  const { agents, loading, couriers } = useAppSelector(
    (state) => state.courier
  );
  const { user } = useAppSelector((state) => state.auth);
  const [courierId, setCourierId] = useState<number | string | undefined>(
    user?.user?.adminCourierOfficeId ?? ""
  );
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const dispatch = useAppDispatch();

  const dropdownOptionsForAgent = [
    {
      label: "Edit agent",
      onClick: (agent: any) => {
        setOpenEdit(true);
        setSelectedAgent(agent);
      },
      returnAll: true,
    },
    {
      label: <span className="text-red-500">Disable agent</span>,
      onClick: (agent: any) => {
        setDisabledAgent(true);
        setSelectedAgent(agent);
      },
      returnAll: true,
    },
  ];

  const agentsData = {
    columns: [
      { label: "Name of Agent", field: "name" },
      { label: "Gender", field: "gender" },
      { label: "Email Address", field: "email" },
      { label: "Phone Number", field: "phone" },
      { label: "Name of Branch", field: "branch" },
      { label: "Reg Date", field: "reg_date" },
      // { label: "Status", field: "status" },
      { label: "Action", field: "action" },
    ],
    rows: agents?.data?.items?.map((item: any, index: number) => {
      return {
        id: index + 1,
        name: item?.agentName,
        email: item?.agentEmail,
        phone: item?.agentPhoneNumber,
        branch: item?.officeName,
        gender: item?.agentGender,
        reg_date: moment(item?.createdAt).format("Do MMM YYYY"),
        status: (
          <div
            className={`px-3 text-center py-1 rounded-full text-sm font-semibold ${
              item?.status === "ACTIVE"
                ? "bg-green-100 text-green-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {item?.status}
          </div>
        ),
        action: (
          <DropdownMenu
            options={dropdownOptionsForAgent}
            icon={<MoreVertical style={{ cursor: "pointer" }} />}
            item={item}
          />
        ),
      };
    }),
  };

  const tabList = [
    { id: 1, name: "All Agent", count: agents?.data?.meta?.totalItems ?? 0 },
  ];

  const handleFilterSelect = (value: number | string | undefined) => {
    setCourierId(value);
  };

  useEffect(() => {
    dispatch(resetCourier());
    dispatch(fetchAllCouriers({}));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchCourierAgents({ courierId, page: page + 1, limit }));
  }, [dispatch, courierId, page, limit]);

  return (
    <>
      <WhiteTitledCard>
        <div className="px-6">
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between mb-4">
            {tabList?.map((tab) => (
              <div
                key={tab.id}
                className={`space-x-2 cursor-pointer text-sm font-semibold p-2 text-black border-b-2 border-primary
                 `}
              >
                <span>{tab.name}</span>
                {
                  <span
                    className={"bg-lightPurple px-2  text-accent rounded-md"}
                  >
                    {tab?.count}
                  </span>
                }
              </div>
            ))}
            <div className="flex-col-reverse sm:flex-row sm:items-center gap-4 flex">
              {user?.user?.roles === "SUPER_ADMIN" && (
                <FilterByButton
                  options={mapDataToLabelValue(
                    couriers?.data?.items,
                    "name",
                    "id"
                  )}
                  onSelect={handleFilterSelect}
                  buttonLabel="Select courier"
                  classNames="min-w-[200px]"
                />
              )}
              <Button
                customStyle={{
                  width: "auto",
                  minWidth: "150px",
                  height: "40px",
                }}
                label="Add agent"
                onClick={() => setOpenAdd(true)}
              />
            </div>
          </div>
          <CustomTable
            data={agentsData}
            noRecordText={
              user?.user?.roles === "SUPER_ADMIN"
                ? "Select Courier to view agents"
                : "No Agent Available"
            }
            loading={loading}
            showFooter={true}
            totalPages={agents?.data?.meta?.totalPages}
            itemsPerPage={agents?.data?.meta?.itemsPerPage}
            currentPage={page}
            onPageChange={(value: number) => setPage(value)}
            onLimitChange={(value: number) => {
              setLimit(value);
              setPage(0);
          }}
          />
        </div>
      </WhiteTitledCard>
      <AddAgentModal
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        onSave={() => {
          dispatch(fetchCourierAgents({ courierId, page: page + 1, limit }));
        }}
      />
      <EditAgentModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        selectedAgent={selectedAgent}
        onSave={() =>
          dispatch(fetchCourierAgents({ courierId, page: page + 1, limit }))
        }
      />
      <DisableAgentModal
        isOpen={disabledAgent}
        onClose={() => setDisabledAgent(false)}
        agentId={selectedAgent?.agentId}
        onConfirm={() =>
          dispatch(fetchCourierAgents({ courierId, page: page + 1, limit }))
        }
      />
    </>
  );
};

export default AgentTable;
